import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/Table";
import { Button } from "@/components/ui/button/Button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select/Select";
import SkeletonTable from "@/components/ui/skeleton/SkeletonTable"
import axios from "axios";
import { RangeDatePicker } from "@/components/ui/calendar/RangeDatePicker";
import { dateQuery } from "@/lib/utils";
import ISemester from "@/models/semester/Semester";
import useSemesters from "@/lib/hooks/useSemesters";
import { Link } from "react-router-dom";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination/Pagination";
import { IRoomLocation } from "@/models/room/RoomLocation";
import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";

type RangeDate = {
  from: Date | undefined;
  to: Date | undefined;
};

type Borrowing = {
  id: number;
  student_nim_leader: string;
  room: { room_code: string } | null;
  created_at: string;
  date: string;
  borrowing_status: string;
  userData: {
    email: string;
  };
};

type Filters = {
  location: number;
  semester: string;
  dateFrom: string;
  dateTo: string;
};

type Asset = {
  id: number;
  student_email: string;
  status: string;
  check_out_date: string;
  check_in_date: string; 
};

const ReportPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<Borrowing[]>([]);
  const [props, setPros] = useState<Asset[]>([]);
  const [filters, setFilters] = useState<Filters>({
    location: -1,
    semester: "",
    dateFrom: "",
    dateTo: "",
  });

  const [locations, setLocations] = useState<IRoomLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<number>(-1);
  const [dateRange, setDateRange] = useState<RangeDate>({
    from: undefined,
    to: undefined,
  });
  const [selectedSemester, setSelectedSemester] = useState<number>(-1);
  const { semesters } = useSemesters();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(1);

  const [activeTab, setActiveTab] = useState<"Room" | "Asset">("Room");
  const [isLoading, setIsLoading] = useState(false);

  const fetchLocation = async () => {
    try {
      const res = await axios.get(
          import.meta.env.VITE_API_URL + "room-location/borrowing", {withCredentials: true}
      );
      setLocations(res.data.data);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    setIsLoading(true);
    try {
      let params: {
        page: number;
        semester_id: string;
        date_start: string;
        date_end: string;
        location_id?: number; 
      } = {
        page: currentPage,
        semester_id: filters.semester,
        date_start: filters.dateFrom,
        date_end: filters.dateTo,
      };

      if (activeTab === "Room" && filters.location) {
        params.location_id = filters.location;
      }

      const endpoint = activeTab === "Room" ? "borrowing/staff/report-room" : "asset-borrowing/staff/report-asset";
      let response;
        response = await axios.get(
          import.meta.env.VITE_API_URL + endpoint,
          { params: params }
        );
      
      if(activeTab === "Asset") {
        setPros(response.data.data ?? []);
      } else {
        setData(response.data.data ?? []);
      }
      setCurrentPage(response.data.current_page);
      setLastPage(response.data.last_page);

    } catch (error) {
      console.error("Failed to fetch report data:", error);
      setData([]);
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLocation();
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [filters, currentPage, activeTab]);

  const handleApplyFilters = () => {
    setCurrentPage(1);

    setFilters({
      location: selectedLocation,
      semester: selectedSemester.toString(),
      dateFrom: dateRange.from ? dateQuery(dateRange.from) : "",
      dateTo: dateRange.to ? dateQuery(dateRange.to) : "",
    });
  };

  const handleClearFilters = () => {
    setSelectedLocation(-1);
    setDateRange({ from: undefined, to: undefined });
    setSelectedSemester(-1);
    setCurrentPage(1);
    setFilters({ location: -1, semester: "", dateFrom: "", dateTo: "" });
  };

  const handleExport = async () => {
    setIsLoading(true);
    let params: {
      semester_id: string;
      date_start: string;
      date_end: string;
      location_id?: string; 
    } = {
      semester_id: selectedSemester.toString(),
      date_start: dateRange.from ? dateQuery(dateRange.from) : "",
      date_end: dateRange.to ? dateQuery(dateRange.to) : "",
    };

    if (activeTab === "Room" && filters.location) {
      params.location_id = selectedLocation.toString();
    }

    const queryString = new URLSearchParams(params).toString();
    const endpoint = activeTab === "Asset" ? "asset-borrowing/staff/export-asset" : "borrowing/staff/export-room";

    try {
      const response = await fetch(import.meta.env.VITE_API_URL + endpoint + "?" + queryString);

      if (!response.ok) {
        throw new Error("Failed to export report");
      }

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      if(activeTab === "Asset") link.download = "asset_borrowings.xlsx";
      else link.download = "room_borrowings.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsLoading(false); 
    }
  };

  const generatePaginationItems = () => {
    const pages = [];
    const showLeftEllipsis = currentPage > 3;
    const showRightEllipsis = currentPage < lastPage - 2;
    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(lastPage - 1, currentPage + 1);

    pages.push(
      <PaginationItem key={1}>
        <PaginationLink
          href="#"
          isActive={currentPage === 1}
          onClick={(e) => {
            e.preventDefault();
            setCurrentPage(1);
          }}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );

    if (showLeftEllipsis) {
      pages.push(
        <PaginationItem key="left-ellipsis">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            href="#"
            isActive={i === currentPage}
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(i);
            }}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (showRightEllipsis) {
      pages.push(
        <PaginationItem key="right-ellipsis">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    if (lastPage > 1) {
      pages.push(
        <PaginationItem key={lastPage}>
          <PaginationLink
            href="#"
            isActive={currentPage === lastPage}
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(lastPage);
            }}
          >
            {lastPage}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return pages;
  };

  const formatBorrowingDate = (dateString: string): string => {
      if (!dateString || typeof dateString !== 'string') {
          return "-";
      }

      const [year, month, day] = dateString.split('-');
      
      const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      
      const formattedDay = String(dateObj.getDate()).padStart(2, '0');
      const formattedMonth = monthNames[dateObj.getMonth()];
      const formattedYear = dateObj.getFullYear();

      return `${formattedDay} ${formattedMonth} ${formattedYear}`;
  };

  return (
    <div className="container mx-auto py-10 px-8 space-y-6">
      <LoadingIndicator loading={isLoading} />
      <div className="flex gap-4 my-4">
        <Button
          variant="ghost"
          className={`btn shadow-lg shadow-secondary px-3 py-2 rounded text-sm font-medium border-2 border-transparent ${activeTab === "Room" ? 'border-blue-500' : ''}`}
          onClick={() => setActiveTab('Room')}
        >
          Room
        </Button>
        <Button
          variant="ghost"
          className={`btn shadow-lg shadow-secondary px-3 py-2 rounded text-sm font-medium border-2 border-transparent ${activeTab === "Asset" ? 'border-blue-500' : ''}`}
          onClick={() => setActiveTab('Asset')}
        >
          Asset
        </Button>
      </div>
      <div className="flex flex-wrap gap-6 items-center bg-secondary p-4 rounded-2xl shadow">
        {activeTab === "Room" && 
          <Select value={selectedLocation.toString()} onValueChange={(v) => setSelectedLocation(+v)}>
              <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                  <SelectItem value="-1">All Locations</SelectItem>
                  {locations.map((type) => (
                      <SelectItem value={type.id.toString()} key={type.id}>
                          {type.location}
                      </SelectItem>
                  ))}
              </SelectContent>
          </Select>
        }
        <Select value={selectedSemester.toString()} onValueChange={(v) => setSelectedSemester(+v)}>
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="All Semesters" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-1">All Semesters</SelectItem>
            {semesters.map((s: ISemester) => (
              <SelectItem value={s.id.toString()} key={s.id}>
                {s.description}
                {s.status === "1" && <>&nbsp;(Current)</>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <RangeDatePicker
          selected={dateRange}
          onChange={setDateRange}
        />

        <Button onClick={handleApplyFilters}>Filter</Button>
        <Button
          variant="outline"
          onClick={handleClearFilters}
        >
          Clear
        </Button>
        <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleExport()}>
          Export to Excel
        </Button>
      </div>
      {activeTab === "Room" ? (
        <motion.div
          className="min-h-[20rem]"
          initial={{ opacity: 0.2, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ease: "linear", duration: 0.4 }}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email Borrower</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Request Date</TableHead>
                <TableHead>Borrowing Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            {loading ? (
              <SkeletonTable rows={10} columns={6} />
            ) : (
              <TableBody>
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center">No data found</TableCell>
                  </TableRow>
                )}
                {data.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.userData.email}</TableCell>
                    <TableCell>{d.room?.room_code ?? "-"}</TableCell>
                    <TableCell>{d.created_at}</TableCell>
                    <TableCell>{formatBorrowingDate(d.date)}</TableCell>
                    <TableCell>{d.borrowing_status}</TableCell>
                    <TableCell>
                      <Link
                        to={`/history/staff/${d.id}`}
                      >
                        <Button variant={"outline"} className="hover:bg-blue-600/90 hover:text-white transition-colors ease-linear duration-200">
                          Detail
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </motion.div>
      ) : (
        <motion.div
          className="min-h-[20rem]"
          initial={{ opacity: 0.2, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            ease: "linear",
            duration: 0.4,
          }}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email Borrower</TableHead>
                <TableHead>Check-Out</TableHead>
                <TableHead>Check-In</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            {loading === true ? (
              <SkeletonTable rows={10} columns={5} />
            ) : (
              <TableBody>
                {props.map((d) => (
                  d && (
                    <TableRow key={d.id}>
                      <TableCell>{d.student_email}</TableCell>
                      <TableCell>{formatBorrowingDate(d.check_in_date)}</TableCell>
                      <TableCell>{formatBorrowingDate(d.check_out_date)}</TableCell>
                      <TableCell>
                        {d.status}
                      </TableCell>
                      <TableCell>
                        <Link to={`/history/asset/staff/${d.id}`}>
                          <Button variant={"outline"} className="hover:bg-blue-600/90 hover:text-white transition-colors ease-linear duration-200">
                            Detail
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  )
                ))}
              </TableBody>
            )}
          </Table>
        </motion.div>
      )}
      <div className="p-4 flex justify-between items-center bg-secondary flex-wrap gap-y-4">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage > 1) setCurrentPage(currentPage - 1);
                }}
              />
            </PaginationItem>

            {generatePaginationItems()}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage < lastPage) setCurrentPage(currentPage + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};

export default ReportPage;